import { useState, useEffect } from "react";

import { TileTitleBasics } from "@/components/molecules/TileTitleBasics";
import { CoreBadge } from "@/components/atoms/CoreBadge";
import {
  CoreBadgeSize,
  CoreBadgeVariant,
} from "@/components/atoms/CoreBadge/types";
import { Image } from "@/components/atoms/Image";
import { TileProgressBar } from "@/components/atoms/TileProgressBar";
import {
  TileProgressBarSize,
  TileProgressBarStyle,
} from "@/components/atoms/TileProgressBar/types";
import { getProviderLogo } from "@/utils/TileUtils";
import { fetchTuneInBadgeDetails } from "@/utils/FetchTuneInBadgeDetails";
import { isSportsEvent } from "@/utils/FetchAssetDetails/types";
import { useClassNames } from "@/utils/classNames";

import { TileMetadataBlockProps } from "./types";
import styles from "./TileMetadataBlock.module.scss";

/**
 * TileMetadataBlock component displays metadata for a media asset, used on 16:9 tiles.
 *
 * @param asset - the asset object containing metadata
 * @param tileSize - the size of the tile this metadata is displayed in
 * @param progress - optional progress percentage to display a progress bar
 * @param className - Additional CSS class for styling
 * @returns the TileMetadataBlock component
 *
 * @example
 * ```tsx
 * <TileMetadataBlock
 *   asset={assets[0]}
 *   tileSize={Tile169Size.Medium}
 *   progress={25}
 * />
 * ```
 */
const TileMetadataBlock = ({
  asset,
  tileSize,
  progress,
  className,
}: TileMetadataBlockProps) => {
  const [tuneInBanner, setTuneInBanner] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      const tuneIn = await fetchTuneInBadgeDetails(asset, false);
      if (typeof tuneIn === "string" && tuneIn.length > 0) {
        setTuneInBanner(tuneIn);
      }
    };
    fetchDetails();
  }, [asset]);

  const formatBadgeText = (): string | null => {
    const badge =
      isSportsEvent(asset) && asset.attributes.eventDetails.eventStage;
    if (badge === "REPLAY") {
      return "Replay";
    } else if (badge === "LIVE") {
      return badge;
    } else {
      return null;
    }
  };

  const cn = useClassNames(
    styles,
    styles["tile-metadata-block"],
    styles[`size-${tileSize}`],
    className,
  );
  const genres = "genres" in asset.attributes ? asset.attributes.genres : [];
  const title = "title" in asset.attributes ? asset.attributes.title : "";
  const providerLogo = getProviderLogo(asset);
  const eyebrow = isSportsEvent(asset) && genres && genres[0];
  const badge = formatBadgeText();

  return (
    <div className={cn}>
      {providerLogo && (
        <div className={styles["provider-logo-container"]}>
          <Image src={providerLogo} className={styles["provider-logo"]} />
        </div>
      )}

      <div className={styles["caption-container"]}>
        {badge && (
          <CoreBadge
            variant={
              badge.toLowerCase() === "live"
                ? CoreBadgeVariant.Live
                : CoreBadgeVariant.White
            }
            size={CoreBadgeSize.Size24}
          >
            {badge}
          </CoreBadge>
        )}

        <div className={styles["caption"]}>
          {eyebrow && <div className={styles["eyebrow"]}>{eyebrow}</div>}
          <div className={styles["title"]}>{title}</div>
          <TileTitleBasics asset={asset} tileSize={tileSize} />
        </div>
      </div>

      {progress && (
        <TileProgressBar
          size={TileProgressBarSize.Medium}
          barStyle={TileProgressBarStyle.Floating}
          progress={progress}
          className={styles["progress-bar"]}
        />
      )}

      {tuneInBanner && (
        <div className={styles["tune-in-banner"]}>{tuneInBanner}</div>
      )}
    </div>
  );
};

export { TileMetadataBlock };
